import React, { useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useServerFeatures } from '../../../../hooks/useServerFeature';
import { Nullable } from '../../../../utils/nullable';
import { useUserInfo } from '../../context/UserInfo';
import { managerRoutes } from '../../manager/routes';
import { routes } from '../../routes';
import {
  ManagerRole,
  useMyAllApprovalRequestsLightQuery,
  usePendingTimeEntryChangeRequestsQuery,
  ApprovalRequestStatus,
  AbsenceApprovalStatus,
  useHasCompensationReviewsQuery,
} from '../../__generated__/graphql';
import {
  HubElement,
  HubElementContentType,
  useHubElements,
} from '../../hooks/useHubElements';
import {
  mapManagerReportsType,
  useManagerReportsMode,
} from '../../hooks/useManagerReportsMode';
import { useIsManagerMode } from '../../hooks/useIsManagerMode';
import { SidebarBadge } from '../SidebarBadge';
import { usePendingAbsenceRequests } from '../../manager/Absences/PendingRequests/usePendingAbsenceRequests';
import { getMenuItems } from './utils';
import {
  MenuHeaderProps,
  MenuItemsSectionType,
  MenuItemType,
  SubItem,
} from '../../../../atomic/molecules/navigation/types';
import MenuBar from '../../../../atomic/organisms/navigation/MenuBar/MenuBar';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import HubElementIcon from '../HubElementIcon/HubElementIcon';
import FlairLogo from '../../../../vendor/img/flair-logo.svg';
import { MenuBarContext } from '../../context/MenuBar';
import { useIsScreenSizeLargerThanOrEqual } from '../../../../hooks/useIsScreenSizeLargerThan';
import './MainMenuBar.css';
import { useMyTrackingTypes } from '../../hooks/timeTracking/useMyTrackingTypes';
import ExpiryWrapper from '../../../../components/ExpiryWrapper/ExpiryWrapper';
import { ExpiryDateEnums } from '../../../../components/ExpiryWrapper/enums/ExpiryDateEnums';

export const MainMenuBar: React.FC = () => {
  const {
    isActive,
    isHiringManager,
    organisation,
    organizationSubscriptionInfo,
    name,
    logoUrl,
    hasCoursesAssigned,
    hasCalibrationSessionsAssigned,
  } = useUserInfo();
  const { isManagerModeAvailable, hasAnyOfManagerRole } = useIsManagerMode();

  const isFlairSchedulerEnabled =
    organizationSubscriptionInfo?.flairScheduler === true;
  const { isTimeTrackingEnabled, isProjectTrackingEnabled } =
    useMyTrackingTypes();

  const isLargeScreen = useIsScreenSizeLargerThanOrEqual('lg');
  const { isExpanded, setIsExpanded } = useContext(MenuBarContext);
  const isMenuBarExpanded = isLargeScreen ? true : isExpanded;

  const isInManagerModeWithRoles = useCallback(
    (roles: ManagerRole[]) => {
      return isManagerModeAvailable && hasAnyOfManagerRole(roles);
    },
    [isManagerModeAvailable, hasAnyOfManagerRole],
  );

  const isFeatureVisible = useServerFeatures();

  const [pendingTimeEntriesReportsMode] = useManagerReportsMode(
    'pendingTimeEntryChangeRequests',
  );
  const [absencesReportsMode] = useManagerReportsMode('absences');
  const pendingAbsencesQuery = usePendingAbsenceRequests(
    mapManagerReportsType(absencesReportsMode),
  );

  const hasPeopleAccess =
    isInManagerModeWithRoles(managerRoutes.myTeamOverview.rolesWithAccess) &&
    isFeatureVisible('People_Tab');
  const { data: compReviewsData } = useHasCompensationReviewsQuery();
  const hasCompensationReviews = !!compReviewsData?.me.hasCompensationReviews;

  const allApprovalRequestsQuery = useMyAllApprovalRequestsLightQuery();

  const approvalRequests =
    allApprovalRequestsQuery.data?.me?.approvalRequests || [];
  const hasApprovalRequests = approvalRequests.length > 0;

  const pendingApprovalRequests = approvalRequests.filter((approvalRequest) => {
    if (approvalRequest.absence) {
      if (
        approvalRequest.absence.flair__Approval_Status__c !==
          AbsenceApprovalStatus.Pending &&
        approvalRequest.flair__Approval_Status__c ===
          ApprovalRequestStatus.Pending
      ) {
        return false;
      }
    }

    return (
      approvalRequest.flair__Approval_Status__c ===
      ApprovalRequestStatus.Pending
    );
  });

  const filterWithAbsence = pendingApprovalRequests.filter(
    (approvalRequest) => {
      if (
        approvalRequest.flair__Related_Object_Name__c !== 'flair__Absence__c'
      ) {
        return true;
      }
      return approvalRequest.absence !== null;
    },
  );

  const pendingApprovalRequestsCount = filterWithAbsence.length;

  const { t } = useTranslation();
  const tNavigationTranslation = useNamespacedTranslation(
    'navigation.menuItems',
  );

  const handleOnNavigationalItemClick = () => {
    setIsExpanded(false);
  };

  const pendingTimeEntryChangeRequestsQuery =
    usePendingTimeEntryChangeRequestsQuery({
      variables: {
        managerReportsType: mapManagerReportsType(
          pendingTimeEntriesReportsMode,
        ),
      },
    });

  const { sidebar: sidebarHubElements, getGroupChildren } = useHubElements();
  const history = useHistory();

  const isActiveHiringManager = isActive && isHiringManager;
  const isHRHelpDeskFeatureVisible = isFeatureVisible('HR_HelpDesk');

  const isLearningFeatureEnabled = hasCoursesAssigned;
  const isCalibrationFeatureEnabled = hasCalibrationSessionsAssigned;

  const isTimeTrackingFeatureEnabled =
    isFeatureVisible('Time_Tracking') && isTimeTrackingEnabled;
  const isProjectsFeatureEnabled =
    isFeatureVisible('Projects') && isProjectTrackingEnabled;
  const isShiftPlannerFeatureEnabled = isFeatureVisible('Shift_Planner');
  const isSchedulerFeatureEnabled =
    isFeatureVisible('Flair_Scheduler') && isFlairSchedulerEnabled;

  const shouldShowTimeMenu =
    isActive &&
    (isTimeTrackingFeatureEnabled ||
      isProjectsFeatureEnabled ||
      isShiftPlannerFeatureEnabled ||
      isSchedulerFeatureEnabled);

  const companySubItems: SubItem[] = [
    {
      label: tNavigationTranslation(`company.subMenuItems.directory.title`),
      route: routes.peopleDirectory.route,
      shouldRender: isActive && isFeatureVisible('Directory'),
    },
    {
      label: tNavigationTranslation(`company.subMenuItems.orgChart.title`),
      route: routes.orgChart.route,
      shouldRender: isActive && isFeatureVisible('Org_Chart'),
    },
    {
      label: tNavigationTranslation(`company.subMenuItems.announcement.title`),
      route: routes.announcements.route,
      shouldRender: isActive && isFeatureVisible('Announcements'),
    },
    {
      label: tNavigationTranslation(`company.subMenuItems.documents.title`),
      route: routes.peopleDocuments.route,
      shouldRender: isFeatureVisible('Corporate_Documents'),
    },
  ];

  const nullablePersonalMenuItems: Nullable<MenuItemType>[] = [
    {
      label: t('dashboard.title'),
      route: routes.home.route,
      shouldRender: true,
      icon: <FlairIcon icon="dashboard-outline" size="lg" />,
      activeIcon: <FlairIcon icon="dashboard-filled" size="lg" />,
    },
    {
      label: tNavigationTranslation(`profile.title`),
      route: routes.personalData.route,
      shouldRender: true,
      icon: <FlairIcon icon="profile-outline" size="lg" />,
      activeIcon: <FlairIcon icon="profile-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(
            `profile.subMenuItems.personalData.title`,
          ),
          route: routes.personalData.route,
          shouldRender: isFeatureVisible('Personal_Data'),
        },
        {
          label: tNavigationTranslation(`profile.subMenuItems.documents.title`),
          route: routes.documentsPersonal.route,
          shouldRender: true,
        },
        {
          label: tNavigationTranslation('inventory.title'),
          route: routes.inventory.route,
          shouldRender: isFeatureVisible('Inventory'),
        },
        {
          label: tNavigationTranslation(`profile.subMenuItems.expenses.title`),
          route: routes.myExpenses.route,
          shouldRender: isActive && isFeatureVisible('Expenses'),
        },
        {
          label: tNavigationTranslation(
            `profile.subMenuItems.certificates.title`,
          ),
          route: routes.myCertificates.route,
          shouldRender: isFeatureVisible('Certificates'),
        },
        {
          label: tNavigationTranslation(
            `profile.subMenuItems.requestForms.title`,
          ),
          route: routes.requestForms.route,
          shouldRender: isActive && isFeatureVisible('Request_Forms'),
        },
      ],
    },
    {
      label: tNavigationTranslation(`company.title`),
      route: routes.announcements.route,
      shouldRender: companySubItems.some((item) => item.shouldRender),
      icon: <FlairIcon icon="company-outline" size="lg" />,
      activeIcon: <FlairIcon icon="company-filled" size="lg" />,
      subItems: companySubItems,
    },
    {
      label: tNavigationTranslation(`tasks.submenuItems.workflows`),
      route: routes.workflowsAllWorkflows.route,
      shouldRender: isFeatureVisible('Workflows'),
      icon: <FlairIcon icon="workflow-outline" size="lg" />,
      activeIcon: <FlairIcon icon="workflow-filled" size="lg" />,
    },
    {
      label: tNavigationTranslation(`personalAbsences.title`),
      route: routes.absences.route,
      shouldRender: isActive && isFeatureVisible('Absences'),
      icon: <FlairIcon icon="absence-outline" size="lg" />,
      activeIcon: <FlairIcon icon="absence-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(
            `personalAbsences.subMenuItems.overview.title`,
          ),
          route: routes.absences.route,
          shouldRender: true,
        },
        {
          label: tNavigationTranslation(
            `personalAbsences.subMenuItems.myAbsences.title`,
          ),
          route: routes.myAbsences.route,
          shouldRender: true,
        },
        {
          label: tNavigationTranslation(
            `personalAbsences.subMenuItems.calendar.title`,
          ),
          route: routes.absencesCalendar.route,
          shouldRender: true,
        },
        {
          label: tNavigationTranslation(
            `personalAbsences.subMenuItems.colleagues.title`,
          ),
          route: routes.teamCalendar.route,
          shouldRender: true,
        },
      ],
    },
    //   show in a main menu item only if the time tracking not enabled
    {
      label: tNavigationTranslation(`time.subMenuItems.projects.title`),
      route: routes.projectsTracking.route,
      shouldRender:
        isActive && isProjectsFeatureEnabled && !isTimeTrackingEnabled,
      icon: <FlairIcon icon="time-outline" size="lg" />,
      activeIcon: <FlairIcon icon="time-filled" size="lg" />,
    },
    {
      label: tNavigationTranslation(`time.title`),
      route: routes.timeTracking.route,
      shouldRender: shouldShowTimeMenu,
      icon: <FlairIcon icon="time-outline" size="lg" />,
      activeIcon: <FlairIcon icon="time-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(`time.subMenuItems.timeTracking.title`),
          route: routes.timeTracking.route,
          shouldRender: isTimeTrackingFeatureEnabled,
        },
        {
          label: tNavigationTranslation(`time.subMenuItems.projects.title`),
          route: routes.projectsTracking.route,
          shouldRender:
            isProjectsFeatureEnabled && isTimeTrackingFeatureEnabled,
        },
        {
          label: t('shiftPlanner.title'),
          route: routes.personalShifts.route,
          shouldRender: isShiftPlannerFeatureEnabled,
        },
        {
          label: t('shifts.subMenuItems.schedulerShifts.title'),
          route: routes.schedulerShifts.route,
          shouldRender: isActive && isSchedulerFeatureEnabled,
        },
      ],
    },
    {
      label: tNavigationTranslation(`personalDevelopment.title`),
      route: routes.performanceReview.route,
      shouldRender: isActive,
      icon: <FlairIcon icon="performance-outline" size="lg" />,
      activeIcon: <FlairIcon icon="performance-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(
            `personalDevelopment.subMenuItems.reviews.title`,
          ),
          route: routes.performanceReview.route,
          shouldRender: isActive && isFeatureVisible('Assessments'),
        },
        {
          label: tNavigationTranslation(
            `personalDevelopment.subMenuItems.goals.title`,
          ),
          route: routes.performanceReviewGoals.route,
          shouldRender: isActive && isFeatureVisible('Goals'),
        },
        {
          label: tNavigationTranslation(
            `personalDevelopment.subMenuItems.skills.title`,
          ),
          route: routes.performanceReviewSkills.route,
          shouldRender: isActive && isFeatureVisible('Skills'),
        },
        {
          label: tNavigationTranslation(
            `personalDevelopment.subMenuItems.learning.title`,
          ),
          route: routes.performanceReviewLearning.route,
          shouldRender:
            isActive &&
            isLearningFeatureEnabled &&
            isFeatureVisible('Learning'),
        },
        {
          label: tNavigationTranslation(
            `personalDevelopment.subMenuItems.calibrations.title`,
          ),
          route: routes.calibration.route,
          shouldRender: isActive && isCalibrationFeatureEnabled,
        },
      ],
    },
    {
      label: tNavigationTranslation(`engagement.title`),
      route: routes.celebrationsFeed.route,
      shouldRender: true,
      icon: <FlairIcon icon="engagement-outline" size="lg" />,
      activeIcon: <FlairIcon icon="engagement-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(`engagement.subMenuItems.cheers.title`),
          route: routes.celebrationsFeed.route,
          shouldRender: isActive && isFeatureVisible('Cheers'),
        },
        {
          label: tNavigationTranslation(
            `engagement.subMenuItems.engagementSurveys.title`,
          ),
          route: routes.performanceReviewEngagementSurveys.route,
          shouldRender: isActive && isFeatureVisible('Surveys'),
        },
      ],
    },
    {
      label: tNavigationTranslation(`recruitment.title`),
      route: routes.employeeEvaluations.route,
      shouldRender: isActive && isFeatureVisible('Recruiting'),
      icon: <FlairIcon icon="recruitment-outline" size="lg" />,
      activeIcon: <FlairIcon icon="recruitment-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(
            `recruitment.subMenuItems.myEvaluations.title`,
          ),
          route: routes.employeeEvaluations.route,
          shouldRender: isActive,
        },
        {
          label: tNavigationTranslation(
            `recruitment.subMenuItems.careerOpportunities.title`,
          ),
          route: routes.careerOpportunities.route,
          shouldRender: isActive,
        },
        {
          label: t('recruitment.jobsPage.title'),
          route: routes.recruitment.route,
          shouldRender: isActiveHiringManager,
        },
      ],
    },
    {
      label: t('approvalRequest.title'),
      route: routes.pendingApprovalRequests.route,
      shouldRender:
        isActive &&
        hasApprovalRequests &&
        isFeatureVisible('Approval_Processes'),
      icon: <FlairIcon icon="checkmark-done-outline" size="lg" />,
      activeIcon: <FlairIcon icon="checkmark-done-filled" size="lg" />,
      suffix: (
        <SidebarBadge loading={allApprovalRequestsQuery.loading}>
          {pendingApprovalRequestsCount}
        </SidebarBadge>
      ),
    },
    {
      label: t('support.title'),
      route: routes.tickets.route,
      shouldRender: isActive && isHRHelpDeskFeatureVisible,
      icon: <FlairIcon icon="help-desk-outline" size="lg" />,
      activeIcon: <FlairIcon icon="help-desk-filled" size="lg" />,
    },
    ...sidebarHubElements.map((element) =>
      buildHubElementMenuItem(
        element,
        t('dashboard.lightningOutWidget.customComponent') ?? '',
        getGroupChildren,
        history,
      ),
    ),
  ];

  const nullableManagerMenuItems: Nullable<MenuItemType>[] = [
    // my team
    {
      label: tNavigationTranslation(`people.title`),
      route: managerRoutes.myTeamOverview.route,
      shouldRender:
        isInManagerModeWithRoles(
          managerRoutes.myTeamOverview.rolesWithAccess,
        ) && isFeatureVisible('People_Tab'),
      icon: <FlairIcon icon="team-outline" size="lg" />,
      activeIcon: <FlairIcon icon="team-filled" size="lg" />,
    },
    {
      label: tNavigationTranslation(`teamAbsences.title`),
      shouldRender:
        isInManagerModeWithRoles(
          managerRoutes.pendingAbsenceRequests.rolesWithAccess,
        ) && isFeatureVisible('Absences'),
      icon: <FlairIcon icon="absence-outline" size="lg" />,
      activeIcon: <FlairIcon icon="absence-filled" size="lg" />,
      route: managerRoutes.pendingAbsenceRequests.route,
      suffix: (
        <SidebarBadge loading={pendingAbsencesQuery.loading}>
          {pendingAbsencesQuery.pendingAbsenceRequestToMe.length}
        </SidebarBadge>
      ),
    },
    {
      label: tNavigationTranslation(`timeManagement.title`),
      route: routes.timeSheetsControllingPending.route,
      shouldRender:
        (isActive ||
          isInManagerModeWithRoles(
            routes.timeSheetsControllingWeek.rolesWithAccess,
          )) &&
        isFeatureVisible('Time_Tracking'),
      icon: <FlairIcon icon="time-outline" size="lg" />,
      activeIcon: <FlairIcon icon="time-filled" size="lg" />,
      subItems: [
        {
          label: tNavigationTranslation(
            `timeManagement.subMenuItems.controlling.title`,
          ),
          route: routes.timeSheetsControllingPending.route,
          shouldRender: isInManagerModeWithRoles(
            routes.timeSheetsControllingPending.rolesWithAccess,
          ),
        },
        {
          label: tNavigationTranslation(
            `timeManagement.subMenuItems.changeRequests.title`,
          ),
          route: routes.pendingTimeEntryChangeRequests.route,
          shouldRender: isInManagerModeWithRoles(
            routes.timeSheetsControllingPending.rolesWithAccess,
          ),
          suffix: (
            <SidebarBadge loading={pendingTimeEntryChangeRequestsQuery.loading}>
              {pendingTimeEntryChangeRequestsQuery.data?.manager
                .pendingTimeEntryChangeRequests.length ?? 0}
            </SidebarBadge>
          ),
        },
        {
          label: tNavigationTranslation(
            `timeManagement.subMenuItems.projects.title`,
          ),
          route: routes.projectsOverviewManager.route,
          shouldRender: isInManagerModeWithRoles(
            routes.projectsControllingTimeSheets.rolesWithAccess,
          ),
        },
      ],
    },

    {
      label: tNavigationTranslation(`teamDevelopment.title`),
      shouldRender: isInManagerModeWithRoles(
        managerRoutes.reviews.rolesWithAccess,
      ),
      icon: <FlairIcon icon="performance-outline" size="lg" />,
      activeIcon: <FlairIcon icon="performance-filled" size="lg" />,
      route: managerRoutes.reviews.route,
      subItems: [
        {
          label: tNavigationTranslation(
            `teamDevelopment.subMenuItems.reviews.title`,
          ),
          route: managerRoutes.reviews.route,
          shouldRender:
            isManagerModeAvailable && isFeatureVisible('Assessments'),
        },
        {
          label: tNavigationTranslation(
            `teamDevelopment.subMenuItems.goals.title`,
          ),
          route: managerRoutes.goals.route,
          shouldRender: isManagerModeAvailable && isFeatureVisible('Goals'),
        },
        {
          label: tNavigationTranslation(
            `teamDevelopment.subMenuItems.skills.title`,
          ),
          route: managerRoutes.skills.route,
          shouldRender: isManagerModeAvailable && isFeatureVisible('Skills'),
        },
        {
          label: tNavigationTranslation(
            `teamDevelopment.subMenuItems.learning.title`,
          ),
          route: managerRoutes.learning.route,
          shouldRender:
            isManagerModeAvailable &&
            isLearningFeatureEnabled &&
            isFeatureVisible('Learning'),
        },
        {
          label: tNavigationTranslation(
            `teamDevelopment.subMenuItems.calibrations.title`,
          ),
          route: routes.calibration.route,
          shouldRender: isManagerModeAvailable && isCalibrationFeatureEnabled,
        },
      ],
    },
    {
      label: tNavigationTranslation(`expenses.title`),
      route: managerRoutes.expenses.route,
      shouldRender:
        isInManagerModeWithRoles(managerRoutes.expenses.rolesWithAccess) &&
        isFeatureVisible('Expenses'),
      icon: <FlairIcon icon="expenses-outline" size="lg" />,
      activeIcon: <FlairIcon icon="expenses-filled" size="lg" />,
    },
    {
      // Standalone entry only for reviewers/approvers WITHOUT Team -> People
      // access who have at least one compensation review to act on. Users with
      // People access reach it via the People subtab instead.
      label: tNavigationTranslation('compensationReviews.title'),
      route: routes.compensationReviewsStandalone.route,
      shouldRender: !hasPeopleAccess && hasCompensationReviews,
      icon: <FlairIcon icon="money-circle" size="lg" />,
      activeIcon: <FlairIcon icon="money-circle" size="lg" />,
    },
    {
      label: tNavigationTranslation('analytics.title'),
      route: managerRoutes.analytics.route,
      shouldRender:
        isInManagerModeWithRoles(managerRoutes.analytics.rolesWithAccess) &&
        isFeatureVisible('Analytics'),
      icon: <FlairIcon icon="chart-bar-outline" size="lg" />,
      activeIcon: <FlairIcon icon="chart-bar-filled" size="lg" />,
      // TODO: Remove suffix prop after ANALYTICS_EXPIRY_DATE
      suffix: (
        <ExpiryWrapper expiryDate={ExpiryDateEnums.ANALYTICS_EXPIRY_DATE}>
          <SidebarBadge>{tNavigationTranslation('sufix.new')}</SidebarBadge>
        </ExpiryWrapper>
      ),
    },
    {
      label: t('support.title'),
      route: managerRoutes.managerTickets.route,
      shouldRender: isManagerModeAvailable && isHRHelpDeskFeatureVisible,
      icon: <FlairIcon icon="help-desk-outline" />,
      activeIcon: <FlairIcon icon="help-desk-filled" />,
    },
  ];

  const personalMenuItems: MenuItemType[] = getMenuItems(
    nullablePersonalMenuItems,
  );
  const managerMenuItems: MenuItemType[] = getMenuItems(
    nullableManagerMenuItems,
  );

  const header: MenuHeaderProps = {
    title: organisation,
    subtitle: name,
    logoSrc: logoUrl ?? FlairLogo,
  };

  const sections: MenuItemsSectionType[] = [
    {
      section: {
        sectionName: t('navigation.sections.personal'),
        visible: true,
      },
      items: personalMenuItems,
    },
    {
      section: {
        sectionName: t('navigation.sections.myTeam'),
        visible: isManagerModeAvailable,
      },
      items: managerMenuItems,
    },
  ];

  return (
    <MenuBar
      header={header}
      sections={sections}
      isExpanded={isMenuBarExpanded}
      hideSmallScreenBurgerMenu={true}
      className="main-menu-bar"
      onNavigationalItemClick={handleOnNavigationalItemClick}
    />
  );
};

type RouterHistory = ReturnType<typeof useHistory>;

const externalLinkSuffix = <FlairIcon icon="external-link-outline" size="sm" />;

const buildHubElementSubItem = (
  child: HubElement,
  history: RouterHistory,
): SubItem => {
  const label = child.flair__Menu_Item_Name__c || child.Name || '';
  switch (child.config.contentType) {
    case HubElementContentType.ExternalUrl:
      return {
        label,
        shouldRender: true,
        route: routes.home.route,
        suffix: externalLinkSuffix,
        onClick: () => {
          if (child.externalUrl) {
            window.open(child.externalUrl, '_blank', 'noopener,noreferrer');
          }
        },
      };
    case HubElementContentType.Flow:
    case HubElementContentType.Lwc:
    default:
      return {
        label,
        shouldRender: true,
        route: routes.home.route,
        onClick: () => {
          history.push(
            routes.customLightningOut.route.create({
              componentUrl: child.flair__Page_Url__c || `default-${child.Id}`,
            }),
          );
        },
      };
  }
};

const buildHubElementMenuItem = (
  element: HubElement,
  fallbackLabel: string,
  getGroupChildren: (parentId: string) => HubElement[],
  history: RouterHistory,
): MenuItemType => {
  const label = element.flair__Menu_Item_Name__c || fallbackLabel;
  const icon = <HubElementIcon iconValue={element.config.icon} size="lg" />;
  const isExternal =
    element.config.contentType === HubElementContentType.ExternalUrl;
  const suffix = isExternal ? externalLinkSuffix : undefined;
  const base = {
    label,
    shouldRender: true,
    icon,
    activeIcon: icon,
    suffix,
  };

  switch (element.config.contentType) {
    case HubElementContentType.Group: {
      const children = getGroupChildren(element.Id)
        .filter((c) => c.config.contentType !== HubElementContentType.Group)
        .map((c) => buildHubElementSubItem(c, history));
      return {
        ...base,
        route: routes.home.route,
        subItems: children,
      };
    }
    case HubElementContentType.ExternalUrl:
      return {
        ...base,
        route: routes.home.route,
        onClick: () => {
          if (element.externalUrl) {
            window.open(element.externalUrl, '_blank', 'noopener,noreferrer');
          }
        },
      };
    case HubElementContentType.Flow:
    case HubElementContentType.Lwc:
    default:
      return {
        ...base,
        route: routes.customLightningOut.route,
        routeParams: {
          componentUrl: element.flair__Page_Url__c || `default-${element.Id}`,
        },
      };
  }
};

export default MainMenuBar;
